import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Link, useLoaderData, useMatch, useMatchRoute, useParams } from '@ui/app-router';
import { ORG_SELECTOR_DROPDOWN_CONTENT_ID, ORG_SWITCHER_DROPDOWN_CONTENT_ID } from '@ui/constants';
import { cn, generateOrgColor, getInitials } from '@ui/primitives';
import { Avatar, AvatarFallback } from '@ui/primitives/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@ui/primitives/dropdown-menu';
import { Skeleton } from '@ui/primitives/skeleton';
import { organization_people_path, organization_settings_path, organization_subscription_path, RouteNames } from '@ui/routes';
import { ChevronDownIcon, CreditCardIcon, SettingsIcon, UsersIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useGetCurrentUserOrganizationList, useGetOrganization } from 'ze-sdk';
import { OrgSwitcher } from './sidebar';
import { OrgSwitcherOptions } from './sidebar/org-switcher-options';
export function OrgSelectorDropdown() {
    const organizationMatch = useMatch({
        from: '/org/$organization',
        shouldThrow: false
    });
    if (organizationMatch) {
        return /*#__PURE__*/ _jsx(OrgSelectorDropdownWithLoaderData, {});
    }
    return /*#__PURE__*/ _jsx(OrgSelectorDropdownContent, {
        orgPermissions: undefined
    });
}
function OrgSelectorDropdownWithLoaderData() {
    const loaderData = useLoaderData({
        // @ts-expect-error - Router route ids from app-router aren't resolved when app-components type-checks in isolation
        from: '/org/$organization',
        strict: false
    });
    const orgPermissions = loaderData === null || loaderData === void 0 ? void 0 : loaderData.permissions;
    return /*#__PURE__*/ _jsx(OrgSelectorDropdownContent, {
        orgPermissions: orgPermissions
    });
}
function OrgSelectorDropdownContent(param) {
    let { orgPermissions } = param;
    var _ref, _ref1, _ref2, _ref3, _ref4;
    var _orgPermissions_org;
    const { organization } = useParams({
        strict: false
    });
    const matchRoute = useMatchRoute();
    const { data: orgData, isLoading: isLoadingOrg } = useGetOrganization({
        organization: organization !== null && organization !== void 0 ? organization : ''
    }, {
        query: {
            enabled: !!organization
        }
    });
    const canUpdateOrganizationSettings = (_ref = orgPermissions === null || orgPermissions === void 0 ? void 0 : (_orgPermissions_org = orgPermissions.org) === null || _orgPermissions_org === void 0 ? void 0 : _orgPermissions_org.updateOrganizationSettings) !== null && _ref !== void 0 ? _ref : false;
    const canManageSubscription = (_ref1 = orgPermissions === null || orgPermissions === void 0 ? void 0 : orgPermissions.canManageSubscription) !== null && _ref1 !== void 0 ? _ref1 : false;
    const { data: organizationListResp } = useGetCurrentUserOrganizationList();
    const organizationList = organizationListResp === null || organizationListResp === void 0 ? void 0 : organizationListResp.entities;
    const [selectedOrg, setSelectedOrg] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isOrgSwitcherOpen, setIsOrgSwitcherOpen] = useState(false);
    useEffect(()=>{
        if (organization && (!selectedOrg || selectedOrg !== organization)) {
            setSelectedOrg(organization);
        }
    }, [
        organization,
        selectedOrg
    ]);
    useEffect(()=>{
        if (!isOpen) {
            return;
        }
        const handleScroll = (event)=>{
            const orgSelectorDropdown = document.getElementById(ORG_SELECTOR_DROPDOWN_CONTENT_ID);
            const orgSwitcherDropdown = document.getElementById(ORG_SWITCHER_DROPDOWN_CONTENT_ID);
            if (!orgSelectorDropdown) {
                setIsOpen(false);
                return;
            }
            const isWithinSelector = isEventWithinDropdown(event, orgSelectorDropdown);
            const isWithinSwitcher = orgSwitcherDropdown ? isEventWithinDropdown(event, orgSwitcherDropdown) : false;
            if (isWithinSelector || isWithinSwitcher) {
                return;
            }
            setIsOpen(false);
        };
        const isEventWithinDropdown = (event, element)=>{
            const target = event.target;
            if (target && element.contains(target)) {
                return true;
            }
            return event.composedPath().some((node)=>node === element);
        };
        window.addEventListener('scroll', handleScroll, true);
        return ()=>{
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [
        isOpen
    ]);
    const isOrganizationRoot = useMemo(()=>{
        if (!organization) {
            return false;
        }
        return !!matchRoute({
            to: RouteNames.ORGANIZATION,
            params: {
                organization
            },
            fuzzy: false
        });
    }, [
        matchRoute,
        organization
    ]);
    if (!organizationList) {
        return null;
    }
    const organizationDetail = orgData === null || orgData === void 0 ? void 0 : orgData.value;
    const orgDisplayName = (_ref2 = (_ref3 = organizationDetail === null || organizationDetail === void 0 ? void 0 : organizationDetail.displayName) !== null && _ref3 !== void 0 ? _ref3 : organization) !== null && _ref2 !== void 0 ? _ref2 : 'Select Organization';
    const orgSubscriptionTier = (_ref4 = organizationDetail === null || organizationDetail === void 0 ? void 0 : organizationDetail.subscriptionTier) !== null && _ref4 !== void 0 ? _ref4 : 'PERSONAL';
    const avatarColor = generateOrgColor(organization !== null && organization !== void 0 ? organization : orgDisplayName);
    const orgInitials = getInitials(orgDisplayName);
    const onChangeOrgHandler = (orgName)=>{
        setSelectedOrg(orgName);
        setIsOpen(false);
    };
    const skeletonTriggerContent = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Skeleton, {
                className: "w-8 h-8 rounded-md"
            }),
            /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-5 w-32"
            })
        ]
    });
    const triggerContent = isLoadingOrg ? skeletonTriggerContent : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Avatar, {
                className: "w-8 h-8 rounded-md",
                children: /*#__PURE__*/ _jsx(AvatarFallback, {
                    className: cn("text-xs font-medium rounded-md bg-[".concat(avatarColor, "]  text-white")),
                    style: {
                        backgroundColor: avatarColor
                    },
                    children: orgInitials
                })
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "font-medium text-foreground",
                children: orgDisplayName
            })
        ]
    });
    const dropdownContent = /*#__PURE__*/ _jsx(DropdownMenuContent, {
        id: ORG_SELECTOR_DROPDOWN_CONTENT_ID,
        side: "bottom",
        align: "start",
        sideOffset: 8,
        className: "w-[--radix-dropdown-menu-trigger-width] min-w-[240px] text-gray-25 border-border ".concat(isOrgSwitcherOpen ? 'rounded-tr-none rounded-br-none' : ''),
        children: organization ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(OrgSwitcher, {
                    selectedOrg: selectedOrg,
                    organizations: organizationList,
                    onOrgChange: onChangeOrgHandler,
                    isOpen: isOrgSwitcherOpen,
                    onOpen: (value)=>{
                        setIsOrgSwitcherOpen(value);
                    },
                    onClose: ()=>{
                        setIsOpen(false);
                    },
                    orgSubscriptionTier: orgSubscriptionTier
                }),
                /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                /*#__PURE__*/ _jsxs(DropdownMenuGroup, {
                    children: [
                        /*#__PURE__*/ _jsx(DropdownMenuLabel, {
                            children: "Organization"
                        }),
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            asChild: true,
                            disabled: !canUpdateOrganizationSettings,
                            children: /*#__PURE__*/ _jsxs(Link, {
                                to: organization_settings_path({
                                    organization: {
                                        name: organization
                                    }
                                }),
                                className: "flex items-center gap-2 cursor-pointer",
                                children: [
                                    /*#__PURE__*/ _jsx(SettingsIcon, {
                                        className: "w-4 h-4"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: "Settings"
                                    })
                                ]
                            })
                        }),
                        canManageSubscription ? /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            asChild: true,
                            children: /*#__PURE__*/ _jsxs(Link, {
                                to: organization_subscription_path({
                                    organization: {
                                        name: organization
                                    }
                                }),
                                className: "flex items-center gap-2 cursor-pointer",
                                children: [
                                    /*#__PURE__*/ _jsx(CreditCardIcon, {
                                        className: "w-4 h-4"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: "Billing"
                                    })
                                ]
                            })
                        }) : null,
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            asChild: true,
                            disabled: !canUpdateOrganizationSettings,
                            children: /*#__PURE__*/ _jsxs(Link, {
                                to: organization_people_path({
                                    organization: {
                                        name: organization
                                    }
                                }),
                                className: "flex items-center gap-2 cursor-pointer",
                                children: [
                                    /*#__PURE__*/ _jsx(UsersIcon, {
                                        className: "w-4 h-4"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: "Manage users"
                                    })
                                ]
                            })
                        })
                    ]
                })
            ]
        }) : /*#__PURE__*/ _jsx(OrgSwitcherOptions, {
            organizations: organizationList,
            onOrgChange: setSelectedOrg,
            onClose: ()=>{
                setIsOpen(false);
            }
        })
    });
    if (organization && isOrganizationRoot) {
        return /*#__PURE__*/ _jsxs(DropdownMenu, {
            open: isOpen,
            onOpenChange: setIsOpen,
            children: [
                /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                    asChild: true,
                    children: /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        className: "flex items-center gap-2 m-0 p-0 relative z-50 cursor-pointer",
                        "aria-haspopup": "menu",
                        "aria-expanded": isOpen,
                        "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER",
                        children: [
                            triggerContent,
                            /*#__PURE__*/ _jsx(ChevronDownIcon, {
                                id: "org-selector-dropdown-trigger",
                                className: "w-5 h-5 text-muted-foreground ring-1 ring-muted-foreground/50 rounded-sm"
                            })
                        ]
                    })
                }),
                dropdownContent
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-2",
        children: [
            organization ? /*#__PURE__*/ _jsx(Link, {
                to: "/org/$organization",
                params: {
                    organization
                },
                className: "flex items-center gap-2",
                children: triggerContent
            }) : /*#__PURE__*/ _jsx("div", {
                className: "flex items-center gap-2",
                children: triggerContent
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex items-center m-0 p-0 relative z-50",
                children: /*#__PURE__*/ _jsxs(DropdownMenu, {
                    open: isOpen,
                    onOpenChange: setIsOpen,
                    children: [
                        /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                            "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER",
                            children: /*#__PURE__*/ _jsx(ChevronDownIcon, {
                                id: "org-selector-dropdown-trigger",
                                className: "w-5 h-5 text-muted-foreground ring-1 ring-muted-foreground/50 rounded-sm"
                            })
                        }),
                        dropdownContent
                    ]
                })
            })
        ]
    });
}
